// @givmo/board — reading a copy of the return provided to the board, its receipts and the board's
// review of it (CBG-YR5-DIST's web half and CBG-YR5-REVIEW; the backend's
// `agent_docs/contracts/board-return-distribution.md` §8 and `board-return-review.md` §4–§6).
//
// ══ WHAT THE READS ARE, AND WHAT THIS FILE COMPOSES FROM THEM ════════════════════════════════════
//
// Facts only. The backend serves the copy, each person sent it, what Board recorded of each (their
// first opening through the director door, a printed copy handed over, a recorded reading of these
// bytes, the mail), and the board's own act linked as its review. It serves no sentence about any of
// it, so the page's lines are composed HERE, in the words file's words, from those facts and nothing
// else:
//
//   * THE HEADLINE (P2, contract §5) is read off `directors_provided_through`, the day the last
//     director recorded at the act was provided the copy, each provision dated on the clock that
//     recorded it. The page never converts an instant into a day to decide it.
//   * THE EMAIL LINE (P4, §6) is read off each person's `email`: the act's own mail carries the
//     copy's own day (`sent_on` equal to `provided_on`), a later one its own day, and every reason a
//     mail didn't go reads the same.
//
// ⚠ NOTHING HERE ANSWERS LINE 11a OR 11b, and nothing reads the return: Board never opens it.
//
// Tolerant, as every Board decode is: a field missing or of the wrong shape never throws. A row that
// can't be read is dropped; a copy whose identity can't be read at all reads as `null`, which the page
// shows as a failed read.

import type {
  BoardDirectorReturnCopyOut,
  BoardReturnCopyOut,
  BoardReturnDistributionOut,
  BoardReturnDistributionSummaryOut,
  BoardReturnEmailOut,
  BoardReturnPersonOut,
  BoardReturnRecipientPreviewOut,
  BoardReturnReviewChoiceOut,
  BoardReturnReviewConsentOut,
  BoardReturnReviewLinkOut,
  BoardReturnReviewMotionOut,
  BoardReturnReviewOut,
  BoardReturnTaxYearOut,
} from '@givmo/api-client';
import { displayLongDate } from '../../lib/display';
import { oneLine, shortDay } from '../conflicts/coi-decode';
import { returnWords as w } from './return-words';

/**
 * A generated model as the decode hands it on: every optional field filled in, `null` where the read
 * left it out (the director wire's shape), all the way down.
 */
type Filled<T> = T extends readonly (infer Item)[]
  ? Filled<Item>[]
  : T extends object
    ? { [Key in keyof T]-?: Filled<Exclude<T[Key], undefined>> }
    : T;

export type ReturnTaxYear = Filled<BoardReturnTaxYearOut>;
/**
 * A person's mail as the page reads it. `not_sent_reason` keeps the served code, ANY code, one this
 * build doesn't know included: a newer code is still a mail that didn't go (the consent page's rule,
 * K-C1), so it is a string here rather than the generated union.
 */
export type ReturnEmail = Omit<Filled<BoardReturnEmailOut>, 'not_sent_reason'> & {
  not_sent_reason: string | null;
};
export type ReturnPerson = Omit<Filled<BoardReturnPersonOut>, 'email'> & {
  email: ReturnEmail | null;
};
export type ReturnCopy = Omit<Filled<BoardReturnDistributionOut>, 'people'> & {
  people: ReturnPerson[];
};
export type ReturnSummary = Filled<BoardReturnDistributionSummaryOut>;
export type ReturnRecipient = Filled<BoardReturnRecipientPreviewOut>;
export type ReturnReview = Filled<BoardReturnReviewOut>;
export type ReturnReviewMotion = Filled<BoardReturnReviewMotionOut>;
export type ReturnReviewConsent = Filled<BoardReturnReviewConsentOut>;
export type ReturnReviewLink = Filled<BoardReturnReviewLinkOut>;
export type ReviewChoice = Filled<BoardReturnReviewChoiceOut>;
export type DirectorReturnCopy = Filled<BoardDirectorReturnCopyOut>;
export type CopyCheck = Filled<BoardReturnCopyOut>;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isDay(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY.test(value);
}

function textOrNull(value: unknown): string | null {
  return isText(value) ? value : null;
}

function dayOrNull(value: unknown): string | null {
  return isDay(value) ? value : null;
}

function texts(value: unknown): string[] {
  return Array.isArray(value) ? value.filter(isText) : [];
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

function rows<T>(value: unknown, read: (row: Record<string, unknown>) => T | null): T[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((row) => {
    if (!isObject(row)) return [];
    const decoded = read(row);
    return decoded === null ? [] : [decoded];
  });
}

/** The body of a read, whether it came bare or in the `{data: …}` envelope. */
function unwrap(body: unknown): unknown {
  return isObject(body) && isObject(body.data) ? body.data : body;
}

// ── The copy ─────────────────────────────────────────────────────────────────────────────────────

export function decodeTaxYear(value: unknown): ReturnTaxYear | null {
  if (!isObject(value) || typeof value.named_year !== 'number') return null;
  if (!isDay(value.begins_on) || !isDay(value.ends_on)) return null;
  return { named_year: value.named_year, begins_on: value.begins_on, ends_on: value.ends_on };
}

function decodeEmail(value: unknown): ReturnEmail | null {
  if (!isObject(value)) return null;
  return {
    sent: value.sent === true,
    sent_at: textOrNull(value.sent_at),
    // Any served code is a mail that didn't go; only none at all is a mail with no outcome.
    not_sent_reason: textOrNull(value.not_sent_reason),
    seat_connected: value.seat_connected === true,
    sent_on: dayOrNull(value.sent_on),
  };
}

function decodePerson(row: Record<string, unknown>): ReturnPerson | null {
  if (!isText(row.board_member_id)) return null;
  const printed = isObject(row.printed_copy)
    ? {
        given_on: dayOrNull(row.printed_copy.given_on),
        recorded_at: textOrNull(row.printed_copy.recorded_at),
        recorded_by_display_name: textOrNull(row.printed_copy.recorded_by_display_name),
        note: textOrNull(row.printed_copy.note),
      }
    : null;
  const read = isObject(row.read_recorded)
    ? {
        acknowledged_at: textOrNull(row.read_recorded.acknowledged_at),
        road: row.read_recorded.road === 'paper' ? ('paper' as const) : ('signed' as const),
        paper_signed_on: dayOrNull(row.read_recorded.paper_signed_on),
      }
    : null;
  return {
    board_member_id: row.board_member_id,
    display_name: textOrNull(row.display_name),
    title_labels: texts(row.title_labels),
    is_director: row.is_director === true,
    seat_active: row.seat_active !== false,
    opened_at: textOrNull(row.opened_at),
    printed_copy: printed,
    read_recorded: read,
    email: decodeEmail(row.email),
    can_email_again: row.can_email_again === true,
    email_again_from: textOrNull(row.email_again_from),
    can_record_printed_copy: row.can_record_printed_copy === true,
    // An older backend leaves it out: the seat is then read as joined, so nothing is claimed.
    seat_joined: row.seat_joined !== false,
  };
}

const KINDS = ['motion', 'written_consent'] as const;
const OUTCOMES = ['carried', 'failed'] as const;
const CONSENT_STATUSES = ['pending', 'executed', 'withdrawn'] as const;

function decodeReviewMotion(value: unknown): ReturnReviewMotion | null {
  if (!isObject(value) || !isText(value.motion_id) || !isText(value.meeting_id)) return null;
  const outcome = OUTCOMES.find((known) => known === value.outcome);
  if (outcome === undefined) return null;
  return {
    motion_id: value.motion_id,
    motion_text: typeof value.motion_text === 'string' ? value.motion_text : '',
    meeting_id: value.meeting_id,
    meeting_title: textOrNull(value.meeting_title),
    meeting_type: textOrNull(value.meeting_type),
    held_on: dayOrNull(value.held_on),
    outcome,
    votes_for: count(value.votes_for),
    votes_against: count(value.votes_against),
    votes_abstaining: count(value.votes_abstaining),
    minutes_record_id: textOrNull(value.minutes_record_id),
    minutes_approved_on: dayOrNull(value.minutes_approved_on),
  };
}

function decodeReviewConsent(value: unknown): ReturnReviewConsent | null {
  if (!isObject(value) || !isText(value.written_consent_id)) return null;
  const status = CONSENT_STATUSES.find((known) => known === value.status);
  if (status === undefined) return null;
  return {
    written_consent_id: value.written_consent_id,
    title: textOrNull(value.title),
    consent_date: dayOrNull(value.consent_date),
    status,
    record_id: textOrNull(value.record_id),
  };
}

/** The live link, or `null`. A link whose act no longer reads (neither half came) is `null` too. */
export function decodeReview(value: unknown): ReturnReview | null {
  if (!isObject(value) || !isText(value.link_id)) return null;
  const kind = KINDS.find((known) => known === value.kind);
  if (kind === undefined) return null;
  const motion = kind === 'motion' ? decodeReviewMotion(value.motion) : null;
  const consent = kind === 'written_consent' ? decodeReviewConsent(value.written_consent) : null;
  if (motion === null && consent === null) return null;
  return {
    link_id: value.link_id,
    kind,
    linked_at: textOrNull(value.linked_at),
    linked_by_display_name: textOrNull(value.linked_by_display_name),
    motion,
    written_consent: consent,
  };
}

function decodeLink(row: Record<string, unknown>): ReturnReviewLink | null {
  if (!isText(row.link_id)) return null;
  const kind = KINDS.find((known) => known === row.kind);
  if (kind === undefined) return null;
  const label = isObject(row.label)
    ? { title: textOrNull(row.label.title), on: dayOrNull(row.label.on) }
    : null;
  return {
    link_id: row.link_id,
    kind,
    motion_id: textOrNull(row.motion_id),
    written_consent_id: textOrNull(row.written_consent_id),
    label,
    linked_at: textOrNull(row.linked_at),
    linked_by_display_name: textOrNull(row.linked_by_display_name),
    unlinked_at: textOrNull(row.unlinked_at),
    unlinked_by_display_name: textOrNull(row.unlinked_by_display_name),
  };
}

/** The receipts read (frame p7), or `null` when its identity can't be read. */
export function decodeCopy(body: unknown): ReturnCopy | null {
  const value = unwrap(body);
  if (!isObject(value) || !isText(value.record_id) || !isDay(value.provided_on)) return null;
  const taxYear = decodeTaxYear(value.tax_year);
  if (taxYear === null) return null;
  const file = isObject(value.file)
    ? {
        file_name: typeof value.file.file_name === 'string' ? value.file.file_name : '',
        page_count: count(value.file.page_count),
        byte_size: count(value.file.byte_size),
        content_type: typeof value.file.content_type === 'string' ? value.file.content_type : '',
        sha256: typeof value.file.sha256 === 'string' ? value.file.sha256 : '',
      }
    : { file_name: '', page_count: 0, byte_size: 0, content_type: '', sha256: '' };
  const withdrawn = isObject(value.withdrawn)
    ? {
        withdrawn_at: textOrNull(value.withdrawn.withdrawn_at),
        withdrawn_by_display_name: textOrNull(value.withdrawn.withdrawn_by_display_name),
        reason: typeof value.withdrawn.reason === 'string' ? value.withdrawn.reason : '',
      }
    : null;
  const providedBy = isObject(value.provided_by)
    ? {
        user_id: typeof value.provided_by.user_id === 'string' ? value.provided_by.user_id : '',
        display_name: textOrNull(value.provided_by.display_name),
      }
    : { user_id: '', display_name: null };
  return {
    record_id: value.record_id,
    form: isText(value.form) ? value.form : '990',
    tax_year: taxYear,
    title: typeof value.title === 'string' ? value.title : '',
    provided_on: value.provided_on,
    provided_at: textOrNull(value.provided_at),
    provided_by: providedBy,
    file,
    source: 'supplied',
    follows_record_id: textOrNull(value.follows_record_id),
    followed_by_record_id: textOrNull(value.followed_by_record_id),
    withdrawn,
    recipient_count: count(value.recipient_count),
    director_count: count(value.director_count),
    directors_provided_count: count(value.directors_provided_count),
    can_withdraw: value.can_withdraw === true,
    can_provide_newer_copy: value.can_provide_newer_copy === true,
    people: rows(value.people, decodePerson),
    directors_provided_through: dayOrNull(value.directors_provided_through),
    review: decodeReview(value.review),
    review_history: rows(value.review_history, decodeLink),
  };
}

/** The recipients' preview for the providing dialog (frame p4), directors first, as served. */
export function decodeRecipients(body: unknown): ReturnRecipient[] {
  const value = unwrap(body);
  return rows(Array.isArray(value) ? value : [], (row) =>
    isText(row.board_member_id)
      ? {
          board_member_id: row.board_member_id,
          display_name: isText(row.display_name) ? row.display_name : '',
          title_labels: texts(row.title_labels),
          is_director: row.is_director === true,
        }
      : null,
  );
}

/** What Board read of an uploaded file (the copy's check), or `null`. */
export function decodeCopyCheck(body: unknown): CopyCheck | null {
  const value = unwrap(body);
  if (!isObject(value) || !isText(value.object_ref) || !isText(value.generation)) return null;
  return {
    object_ref: value.object_ref,
    generation: value.generation,
    file_name: typeof value.file_name === 'string' ? value.file_name : '',
    content_type: typeof value.content_type === 'string' ? value.content_type : '',
    byte_size: count(value.byte_size),
    page_count: count(value.page_count),
    sha256: typeof value.sha256 === 'string' ? value.sha256 : '',
  };
}

/** The picker's choices: the board's own acts the link may name (§3.1), as served. */
export function decodeChoices(body: unknown): ReviewChoice[] {
  const value = unwrap(body);
  return rows(Array.isArray(value) ? value : [], (row) => {
    const kind = KINDS.find((known) => known === row.kind);
    if (kind === undefined || !isText(row.id)) return null;
    const outcome = OUTCOMES.find((known) => known === row.outcome) ?? null;
    const consentStatus =
      row.consent_status === 'pending' || row.consent_status === 'executed'
        ? row.consent_status
        : null;
    return {
      kind,
      id: row.id,
      title: textOrNull(row.title),
      detail: textOrNull(row.detail),
      meeting_id: textOrNull(row.meeting_id),
      on: dayOrNull(row.on),
      outcome,
      votes_for: typeof row.votes_for === 'number' ? row.votes_for : null,
      votes_against: typeof row.votes_against === 'number' ? row.votes_against : null,
      votes_abstaining: typeof row.votes_abstaining === 'number' ? row.votes_abstaining : null,
      consent_status: consentStatus,
    };
  });
}

/** The director's own copy page (frame p6), or `null`. */
export function decodeDirectorCopy(body: unknown): DirectorReturnCopy | null {
  const value = unwrap(body);
  if (!isObject(value) || !isText(value.record_id) || !isDay(value.provided_on)) return null;
  const taxYear = decodeTaxYear(value.tax_year);
  if (taxYear === null) return null;
  const file = isObject(value.file)
    ? {
        file_name: typeof value.file.file_name === 'string' ? value.file.file_name : '',
        page_count: count(value.file.page_count),
        byte_size: count(value.file.byte_size),
        content_type: typeof value.file.content_type === 'string' ? value.file.content_type : '',
      }
    : { file_name: '', page_count: 0, byte_size: 0, content_type: '' };
  const acknowledgment = isObject(value.acknowledgment)
    ? {
        acknowledged_at: textOrNull(value.acknowledgment.acknowledged_at),
        road: value.acknowledgment.road === 'paper' ? ('paper' as const) : ('signed' as const),
        paper_signed_on: dayOrNull(value.acknowledgment.paper_signed_on),
      }
    : null;
  const withdrawn = isObject(value.withdrawn)
    ? {
        withdrawn_at: textOrNull(value.withdrawn.withdrawn_at),
        reason: typeof value.withdrawn.reason === 'string' ? value.withdrawn.reason : '',
      }
    : null;
  return {
    record_id: value.record_id,
    form: isText(value.form) ? value.form : '990',
    tax_year: taxYear,
    title: typeof value.title === 'string' ? value.title : '',
    provided_on: value.provided_on,
    provided_by_display_name: textOrNull(value.provided_by_display_name),
    file,
    content_hash: typeof value.content_hash === 'string' ? value.content_hash : '',
    is_recipient: value.is_recipient === true,
    opened_at: textOrNull(value.opened_at),
    acknowledgment,
    withdrawn,
    newer_copy_record_id: textOrNull(value.newer_copy_record_id),
  };
}

// ── The words composed from the facts ────────────────────────────────────────────────────────────

/** "April 1, 2027", kept on one line wherever a sentence wraps. */
export function longDate(day: string): string {
  return oneLine(displayLongDate(day) ?? day);
}

/** "2.3 MB", "640 KB": the file's size as the frames write it. */
export function fileSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${bytes} bytes`;
}

/**
 * The receipts' headline (P2, contract §5), or `null` when no director was recorded at the act. The
 * day is the read's own: `directors_provided_through` is the day the last director was provided the
 * copy, `null` until every one of them was.
 */
export function headline(copy: {
  provided_on: string;
  director_count: number;
  directors_provided_count: number;
  directors_provided_through: string | null;
}): string | null {
  if (copy.director_count === 0) return null;
  const through = copy.directors_provided_through;
  if (through !== null) {
    return through === copy.provided_on
      ? w.everyDirectorOn(longDate(copy.provided_on))
      : w.everyDirectorBy(longDate(through));
  }
  return w.someDirectors(
    longDate(copy.provided_on),
    copy.directors_provided_count,
    copy.director_count,
  );
}

/** The person's name as the page says it, never an id. */
export function personName(person: { display_name: string | null }): string {
  return person.display_name ?? 'Someone whose name isn’t shown';
}

/**
 * The email line above the people (P4, contract §6): the act's own mail names the copy's day once,
 * and only the exceptions follow, one each, in the people's order. Every reason a mail didn't go
 * reads the same. A person with no mail row (none expected) is left out.
 */
export function emailLines(copy: ReturnCopy): { lead: string | null; exceptions: string[] } {
  const withMail = copy.people.filter((person) => person.email !== null);
  // A mail accepted with no day served (none expected) is read as the act's own: the day the copy
  // was provided is the one the read would have named.
  const actsOwn = withMail.filter(
    (person) =>
      person.email?.sent === true &&
      (person.email.sent_on === null || person.email.sent_on === copy.provided_on),
  );
  const others = withMail.filter((person) => !actsOwn.includes(person));
  const exception = (person: ReturnPerson): string => {
    const email = person.email;
    const name = personName(person);
    if (email?.sent === true && email.sent_on !== null) {
      return w.emailedLaterOn(name, longDate(email.sent_on));
    }
    return email?.not_sent_reason !== null ? w.emailDidntGo(name) : w.cantConfirm(name);
  };
  if (withMail.length === 0) return { lead: null, exceptions: [] };
  if (others.length === 0)
    return { lead: w.eachEmailedOn(longDate(copy.provided_on)), exceptions: [] };
  if (actsOwn.length > 0) {
    return {
      lead: w.eachEmailedExcept(longDate(copy.provided_on)),
      exceptions: others.map(exception),
    };
  }
  // No one had the act's own mail. When every one of them simply didn't go, one line says so.
  const noneWent = others.every(
    (person) => person.email?.sent !== true && person.email?.not_sent_reason !== null,
  );
  return noneWent
    ? { lead: w.noOneEmailed, exceptions: [] }
    : { lead: null, exceptions: others.map(exception) };
}

/** The person's line under their name: their titles, and the approved words that apply now. */
export function personFacts(person: ReturnPerson): string[] {
  return [
    ...person.title_labels,
    person.is_director ? null : w.notADirector,
    person.seat_active ? null : w.leftTheBoard,
    person.seat_active && !person.seat_joined ? w.hasntJoined : null,
  ].filter((part): part is string => part !== null && part.length > 0);
}

/** The "Recorded reading it" cell: the day it was recorded, or the paper's own day. */
export function readCell(person: ReturnPerson): string | null {
  const read = person.read_recorded;
  if (read === null) return null;
  if (read.road === 'paper') {
    return read.paper_signed_on === null
      ? shortDay(read.acknowledged_at)
      : w.onPaper(shortDay(read.paper_signed_on));
  }
  return read.acknowledged_at === null ? null : shortDay(read.acknowledged_at);
}

/** The board's review, as one line naming the act: the meeting by its own title and day. */
export function reviewName(review: ReturnReview): string {
  if (review.motion !== null) {
    const day = review.motion.held_on === null ? null : longDate(review.motion.held_on);
    const title = review.motion.meeting_title ?? '';
    return day === null ? title : w.meetingAndDay(title, day);
  }
  return review.written_consent?.title ?? w.untitledConsent;
}

/** A removed link, said by the label its act still carries. */
export function removedLinkLine(link: ReturnReviewLink): string | null {
  if (link.unlinked_at === null) return null;
  const removedOn = shortDay(link.unlinked_at);
  const label = link.label;
  if (label === null || label.title === null) return w.removedLinkUnlabelled(removedOn);
  return w.removedLink(
    label.on === null ? label.title : w.meetingAndDay(label.title, longDate(label.on)),
    removedOn,
  );
}

// ── The director's home row (frame p5) ───────────────────────────────────────────────────────────

export interface ReturnToRead {
  recordId: string;
  form: string;
  namedYear: number;
  providedOn: string;
}

/** The attention read's `return_to_read` items: a copy this seat hasn't opened or recorded reading. */
export function returnsToRead(items: readonly unknown[] | null | undefined): ReturnToRead[] {
  return (items ?? []).flatMap((item) => {
    if (!isObject(item) || item.kind !== 'return_to_read') return [];
    if (!isText(item.record_id) || typeof item.named_year !== 'number') return [];
    if (!isDay(item.provided_on)) return [];
    return [
      {
        recordId: item.record_id,
        form: isText(item.form) ? item.form : '990',
        namedYear: item.named_year,
        providedOn: item.provided_on,
      },
    ];
  });
}

// ── Line 11a and line 11b (the Form 990 read's facts) ────────────────────────────────────────────

/** The receipts' counts for one copy, as line 11a's opened state says them. */
export function countsLine(copy: {
  recipient_count: number;
  opened_count: number;
  printed_copy_count: number;
  read_recorded_count: number;
}): string {
  return w.receiptCounts(
    copy.opened_count,
    copy.printed_copy_count,
    copy.read_recorded_count,
    copy.recipient_count,
  );
}
